import { motion, AnimatePresence } from 'framer-motion'
import { X, QrCode, Download } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { nip19 } from 'nostr-tools'

interface ProfileQRModalProps {
    isOpen: boolean
    onClose: () => void
    pubkey: string
    profileName: string
}

export function ProfileQRModal({ isOpen, onClose, pubkey, profileName }: ProfileQRModalProps) {
    if (!isOpen) return null

    const npub = nip19.npubEncode(pubkey)
    const profileUrl = `${window.location.origin}/user/${npub}`

    const handleDownload = () => {
        const svg = document.getElementById('profile-qr-code')
        if (!svg) return

        const svgData = new XMLSerializer().serializeToString(svg)
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const img = new Image()

        img.onload = () => {
            canvas.width = img.width
            canvas.height = img.height
            ctx?.drawImage(img, 0, 0)
            const pngFile = canvas.toDataURL('image/png')

            const downloadLink = document.createElement('a')
            downloadLink.download = `${profileName}-qr.png`
            downloadLink.href = pngFile
            downloadLink.click()
        }

        img.src = 'data:image/svg+xml;base64,' + btoa(svgData)
    }

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-white dark:bg-surface-900 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-surface-200 dark:border-surface-700"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between bg-gradient-to-r from-mavi-50 to-surface-50 dark:from-mavi-900/10 dark:to-surface-800">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-mavi-500/10 rounded-xl flex items-center justify-center">
                                <QrCode className="w-5 h-5 text-mavi-600 dark:text-mavi-400" />
                            </div>
                            <div>
                                <h2 className="font-poppins font-bold text-lg text-gray-900 dark:text-white">Profil QR Kodu</h2>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{profileName}</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* QR Code */}
                    <div className="p-8 flex flex-col items-center gap-6">
                        <div className="bg-white p-6 rounded-2xl shadow-lg">
                            <QRCodeSVG
                                id="profile-qr-code"
                                value={profileUrl}
                                size={256}
                                level="H"
                                includeMargin={true}
                            />
                        </div>

                        <div className="text-center space-y-2">
                            <p className="text-sm font-poppins font-bold text-gray-900 dark:text-white">
                                Profili Paylaş
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-mono break-all px-4">
                                {npub.slice(0, 20)}...{npub.slice(-20)}
                            </p>
                        </div>

                        <button
                            onClick={handleDownload}
                            className="flex items-center gap-2 px-6 py-3 bg-mavi-500 hover:bg-mavi-600 text-white rounded-xl transition-colors font-poppins font-bold text-sm shadow-lg shadow-mavi-500/20"
                        >
                            <Download className="w-4 h-4" />
                            QR Kodu İndir
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    )
}
